import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { BusinessUnitCreditCardInformationModel } from '../business-unit-credit-card-information.model';
import { AppTextService } from '@fi-shared/app-text';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { BillingService } from '../../billing.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { BillingContactUsingType } from '../../billing-contact-type';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IContentLanguage } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { FiCustomValidatorPatterns } from '@fi-shared/fi-custom-validator-patterns/fi-custom-validator-patterns';

@Component({
    selector: 'fi-business-unit-credit-card-information-edit',
    templateUrl: './business-unit-credit-card-information-edit.component.html',
    styleUrls: ['./business-unit-credit-card-information-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-business-unit-credit-card-information-edit' },
    standalone: false,
})
export class BusinessUnitCreditCardInformationEditComponent implements OnInit, OnDestroy {
    @Input() creditCardInformation: BusinessUnitCreditCardInformationModel;

    contactsUsingType = BillingContactUsingType.Reference;
    editTabs: EditTabsModel;
    languageListData = new DropdownSettingsModelV2<IContentLanguage>({
        filterBy: 'Name',
    });
    currencyListData = new DropdownSettingsModelV2<INameIdObj>({
        filterBy: 'Name',
    });

    FiCustomValidatorPatterns = FiCustomValidatorPatterns;

    constructor(
        private appTextService: AppTextService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiLoadingService: LoadingService,
        private billingService: BillingService,
        private stateService: FiStateService,
        private objectTitleService: ObjectTitleService,
        private contentLanguages: ContentLanguagesService,
        private detectDevicesService: DetectDevicesService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.setSelectsDataSets();
        this.setPageHeaderData();
        this.saveChangesWarningService.start(() => this.creditCardInformation);
        this.subscribeOnResize();
    }

    private subscribeOnResize() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.cdr.markForCheck());
    }

    isMobileSmall(): boolean {
        return this.detectDevicesService.isMobileSmall();
    }

    private setSelectsDataSets() {
        this.languageListData.setDataSet(this.contentLanguages.GetApplicationLanguages());
        this.currencyListData.setDataSet(this.billingService.GetBillingCurrencies());
    }

    private setPageHeaderData() {
        this.editTabs = new EditTabsModel(
            this.objectTitleService.getEditTitle(this.appTextService.get('billing.credit_card_info')),
        );
        this.editTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
    }

    save() {
        this.fiLoadingService
            .trackPromise(this.billingService.SaveBusinessUnitPaymentInformation(this.creditCardInformation))
            .then(() => {
                this.saveChangesWarningService.stop();
                this.stateService.go(FiStateNames.Billing.Payment.List);
            });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
